/**
 * skyMask — the shared "is this pixel the sky?" test for depth-aware post passes.
 *
 * WHY THIS EXISTS (2026-09-14). Three passes each rolled their own sky test as
 * `step(0.999, rawDepth)`, reading the comment "sky depth ≈ 1.0" as if 0.999
 * were a near-miss of the far plane. It is not. Hardware depth is NON-LINEAR:
 * for a perspective camera with far ≫ near, `1 − d ≈ near / z`, so
 *
 *     z(d) = near / (1 − d)
 *
 * and with Atlas's `near = 0.05 m`, `0.999` selects **z ≈ 50 m** — almost
 * independently of the far plane. Every one of those passes was therefore
 * classifying all geometry past arm's reach as sky:
 *
 *   • aerialComposite — masked the haze OFF beyond 50 m, i.e. exactly inverted:
 *     distance haze applied only to the nearest 50 m, distant terrain left
 *     crisp. Live on the default (high) tier and above.
 *   • GodRaysPass    — no geometry past 50 m occluded a shaft, so rays shone
 *     through distant mountains and buildings.
 *   • clouds composite — clouds painted over everything past 50 m rather than
 *     over sky only.
 *
 * THE TEST. Sky pixels are the pixels nothing was drawn to, so they still hold
 * the depth CLEAR value. three renders `scene.background` / `backgroundNode`
 * with `depthWrite:false`, so the sky leaves depth untouched at 1.0, and a
 * `depth >= 1.0` test separates it exactly. That is preferred over a "close to
 * 1.0" epsilon (fragile — one quantum of a 24-bit depth buffer at these planes
 * is already ~15 km of view distance) and over linearising to compare against
 * the far plane (same answer, but needs near/far uniforms plumbed into every
 * caller and loses precision exactly where the test lives).
 *
 * ASSUMPTION. Standard depth convention: 0 at the near plane, 1 at the far
 * plane, cleared to 1. This engine does not enable reversed-Z anywhere (two
 * stale comments in Atlas claim otherwise — they are wrong). If reversed depth
 * is ever turned on, the clear value becomes 0 and THIS FILE is the one place
 * that has to change.
 */

import { float, step } from 'three/tsl';

type AnyNode = any;

/**
 * The depth value a pixel holds when no geometry wrote to it (the clear value
 * under the standard depth convention).
 */
export const SKY_CLEAR_DEPTH = 1.0;

/**
 * Reduce a depth sample to its scalar channel.
 *
 * A depth texture sample comes back as a vec4; feeding that straight into
 * `step()` (or adding it to a float accumulator) is a type mismatch that fails
 * WGSL/GLSL codegen. Callers that already hold a scalar are unaffected: `.r`
 * on a float builds a SplitNode, and three's SplitNode.generate ignores the
 * components when its input is float/integer, so it codegens back to the
 * scalar unchanged.
 */
export function depthScalar(depthNode: AnyNode): AnyNode {
  return (depthNode as { r?: AnyNode })?.r ?? depthNode;
}

/**
 * 1.0 where the pixel is sky (nothing drawn), 0.0 where it is geometry.
 *
 * Accepts either a raw depth sample (vec4) or an already-reduced scalar.
 */
export function skyMask(depthNode: AnyNode): AnyNode {
  return step(float(SKY_CLEAR_DEPTH), depthScalar(depthNode) as AnyNode);
}

/**
 * 1.0 where the pixel is geometry, 0.0 where it is sky — the complement of
 * {@link skyMask}, for passes that shade surfaces rather than the background.
 */
export function geometryMask(depthNode: AnyNode): AnyNode {
  return skyMask(depthNode).oneMinus();
}

/**
 * CPU mirror of the hardware depth → view-distance mapping (exported for
 * tests, and so the arithmetic in this file's header is executable rather than
 * asserted).
 *
 *   viewZ = (near · far) / ((far − near) · d − far)      [three's
 *   distance = −viewZ                                     perspectiveDepthToViewZ]
 *
 * This is why a raw-depth threshold is a DISTANCE threshold scaled by `near`:
 * `depthToViewDistance(0.999, 0.05, 120000) ≈ 50`, not 120000.
 */
export function depthToViewDistance(depth: number, near: number, far: number): number {
  return (near * far) / (far - (far - near) * depth);
}
